"use client";

import { Heart, Music, CalendarDays, Clock, Share } from "lucide-react";
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from "@/components/ui/carousel";
import Image from "next/image";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import Grainient from "@/components/Grainient";
import { useState, useEffect, useMemo } from "react";
import { intervalToDuration } from "date-fns";
import NumberFlow from "@number-flow/react";
import { Logo } from "@/components/logo";
import { ModeToggle } from "@/components/mode-toggle";
import { Button } from "@/components/ui/button";
import { useTranslations, useLocale } from "next-intl";

const heroContainer = {
	hidden: { opacity: 0 },
	show: {
		opacity: 1,
		transition: { delayChildren: 0.15, staggerChildren: 0.12 },
	},
};

const heroItem = {
	hidden: { opacity: 0, y: 24 },
	show: { opacity: 1, y: 0 },
};

const cardReveal = {
	hidden: { opacity: 0, y: 32 },
	show: {
		opacity: 1,
		y: 0,
		transition: { type: "spring" as const, stiffness: 120, damping: 20 },
	},
};

const FLOATING_HEARTS = [
	{ left: "8%", size: 14, duration: 12, delay: 0 },
	{ left: "18%", size: 20, duration: 14, delay: 2 },
	{ left: "28%", size: 12, duration: 11, delay: 4 },
	{ left: "42%", size: 16, duration: 13, delay: 1 },
	{ left: "55%", size: 18, duration: 15, delay: 3 },
	{ left: "68%", size: 14, duration: 12, delay: 5 },
	{ left: "78%", size: 22, duration: 16, delay: 2.5 },
	{ left: "88%", size: 10, duration: 10, delay: 1.5 },
	{ left: "15%", size: 11, duration: 13, delay: 6 },
	{ left: "52%", size: 15, duration: 14, delay: 7 },
	{ left: "92%", size: 18, duration: 11, delay: 0.5 },
];

type DurationUnit = {
	value: number;
	label: string;
	labelPlural: string;
};

function RelationshipTimer({
	relationshipDate,
	isPreview = false,
}: {
	relationshipDate: string;
	isPreview?: boolean;
}) {
	const t = useTranslations("LovePage");
	const [duration, setDuration] = useState<{
		years: number;
		months: number;
		days: number;
		hours: number;
		minutes: number;
		seconds: number;
	} | null>(null);

	useEffect(() => {
		const startDate = new Date(`${relationshipDate}T00:00:00`);

		const updateDuration = () => {
			const now = new Date();
			const result = intervalToDuration({ start: startDate, end: now });
			setDuration({
				years: result.years ?? 0,
				months: result.months ?? 0,
				days: result.days ?? 0,
				hours: result.hours ?? 0,
				minutes: result.minutes ?? 0,
				seconds: result.seconds ?? 0,
			});
		};

		updateDuration();
		const interval = setInterval(updateDuration, 1000);

		return () => clearInterval(interval);
	}, [relationshipDate]);

	const units = useMemo<DurationUnit[]>(() => {
		if (!duration) return [];
		return [
			{ value: duration.years, label: t("time.year"), labelPlural: t("time.years") },
			{ value: duration.months, label: t("time.month"), labelPlural: t("time.months") },
			{ value: duration.days, label: t("time.day"), labelPlural: t("time.days") },
			{ value: duration.hours, label: t("time.hour"), labelPlural: t("time.hours") },
			{ value: duration.minutes, label: t("time.minute"), labelPlural: t("time.minutes") },
			{ value: duration.seconds, label: t("time.second"), labelPlural: t("time.seconds") },
		];
	}, [duration, t]);

	if (!duration) return null;

	// Filter out units with 0 values at the start (e.g., if less than 1 year, don't show years)
	const firstNonZeroIndex = units.findIndex((u) => u.value > 0);
	const displayUnits =
		firstNonZeroIndex >= 0 ? units.slice(firstNonZeroIndex) : units.slice(-3);

	return (
		<div
			className={cn(
				"flex flex-wrap justify-center gap-2",
				isPreview ? "grid-cols-3" : "grid-cols-3 sm:grid-cols-6",
			)}
		>
			{displayUnits.map((unit, index) => (
				<motion.div
					key={unit.label}
					className="flex flex-col flex-1 items-center justify-center rounded-xl bg-linear-to-br from-primary/10 to-primary/5 border border-primary/20 p-3"
					initial={{ opacity: 0, scale: 0.8 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ delay: index * 0.1 }}
				>
					<NumberFlow
						value={unit.value}
						className={cn(
							"text-2xl font-bold tabular-nums text-foreground/80",
							isPreview && "text-lg",
						)}
						transformTiming={{ duration: 500, easing: "ease-out" }}
						spinTiming={{ duration: 500, easing: "ease-out" }}
					/>
					<span
						className={cn(
							"text-xs text-muted-foreground mt-1",
							isPreview && "text-[10px]",
						)}
					>
						{unit.value === 1 ? unit.label : unit.labelPlural}
					</span>
				</motion.div>
			))}
		</div>
	);
}

export type LovePageViewProps = {
	title: string;
	message: string;
	coupleName1: string;
	coupleName2: string;
	relationshipDate: string;
	musicUrl: string;
	imageUrls: string[];
	isPreview?: boolean;
};

function getYoutubeEmbedUrl(url: string | null) {
	if (!url) return null;
	let id: string | null = null;

	try {
		const parsed = new URL(url.trim());
		const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
		const parts = parsed.pathname.split("/").filter(Boolean);

		if (host === "youtu.be") {
			id = parts[0] ?? null;
		} else if (host === "youtube.com" || host === "m.youtube.com") {
			if (parsed.pathname === "/watch") {
				id = parsed.searchParams.get("v");
			} else if (
				parts[0] === "embed" ||
				parts[0] === "shorts" ||
				parts[0] === "live"
			) {
				id = parts[1] ?? null;
			}
		}
	} catch {
		// Ignore malformed URLs; fallback below keeps behavior predictable.
	}

	if (!id) {
		id = url.split("v=")[1]?.split("&")[0] ?? null;
	}

	if (!id) return null;
	return `https://www.youtube.com/embed/${id}`;
}

export function LovePageView({
	title,
	message,
	coupleName1,
	coupleName2,
	relationshipDate,
	musicUrl,
	imageUrls,
	isPreview = false,
}: LovePageViewProps) {
	const t = useTranslations("LovePage");
	const common = useTranslations("Common");
	const locale = useLocale();
	
	const names = [coupleName1, coupleName2].filter(Boolean).join(" & ") || "";
	const dateLabel = relationshipDate
		? new Date(`${relationshipDate}T00:00:00`).toLocaleDateString(locale, {
				day: "numeric",
				month: "long",
				year: "numeric",
			})
		: null;
	const daysTogether = relationshipDate
		? Math.floor(
				(Date.now() - new Date(`${relationshipDate}T00:00:00`).getTime()) /
					(1000 * 60 * 60 * 24),
			)
		: null;

	return (
		<>
			<div className="relative min-h-dvh overflow-hidden z-10">
				{/* Banner hero - full width */}
				<motion.header
					className={cn(
						"relative grid py-12 pb-10 place-content-center md:py-24",
						isPreview &&
							"min-h-[25vh] overflow-visible sm:min-h-[25vh] md:min-h-[35vh] pb-6",
						!isPreview && "overflow-hidden",
					)}
					initial="hidden"
					animate="show"
					variants={heroContainer}
				>
					{!isPreview && (
						<div className="absolute inset-0 opacity-90 -mr-1">
							<Grainient
								color1="#fd5c63"
								color2="#893035"
								color3="#DE040F"
								timeSpeed={0.2}
								warpSpeed={0.3}
								grainAmount={0.1}
								className="w-full h-full"
							/>
						</div>
					)}
					{isPreview && (
						<div className="absolute inset-0 bg-linear-to-b from-primary to-primary/60" />
					)}
					{/* <div className="absolute inset-0 bg-linear-to-b from-primary to-primary/50"></div> */}
					{/* Floating hearts loop */}
					<div
						className="absolute inset-0 z-1 pointer-events-none overflow-hidden"
						aria-hidden
					>
						{FLOATING_HEARTS.map((config) => (
							<motion.div
								key={`heart-${config.left}-${config.duration}-${config.delay}`}
								className="absolute bottom-0 text-white/50"
								style={{
									left: config.left,
									width: config.size,
									height: config.size,
								}}
								animate={{
									y: ["100%", "-30vh"],
									opacity: [0, 0.55, 0.55, 0],
									x: [0, 12, -8, 4],
								}}
								transition={{
									duration: config.duration,
									delay: config.delay,
									repeat: Number.POSITIVE_INFINITY,
									repeatDelay: 0,
								}}
							>
								<Heart
									className="w-full h-full fill-current"
									strokeWidth={1.5}
								/>
							</motion.div>
						))}
					</div>
					<div className="relative z-40 px-4 py-8 text-center">
						<motion.h1
							variants={heroItem}
							className={cn(
								"text-5xl z-20 px-4 rounded-md font-bold tracking-tighter text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)] sm:text-5xl md:text-6xl lg:text-7xl",
								isPreview && "text-lg sm:text-xl md:text-2xl lg:text-3xl",
							)}
						>
							{title}
						</motion.h1>
						<motion.p
							variants={heroItem}
							className={cn(
								"mt-2 text-lg font-medium text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.3)] sm:mt-3 sm:text-xl md:text-2xl",
								isPreview && "text-sm sm:text-base md:text-lg",
							)}
						>
							{names}
						</motion.p>
						{dateLabel &&
							daysTogether != null &&
							(isPreview ? (
								<p className="mt-1 text-xs text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)] sm:text-sm">
									{t("since", { date: dateLabel })}
								</p>
							) : (
								<motion.p
									variants={heroItem}
									className="mt-1 text-sm text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)] sm:text-base"
								>
									{t("since", { date: dateLabel })}
								</motion.p>
							))}
					</div>
					{!isPreview && (
						<div className="absolute top-0 right-0">
							<div className="p-4 flex items-center gap-2 justify-end">
								<Button
									size="icon"
									variant="outline"
									className="dark text-white size-12"
								>
									<Share className="h-4 w-4" />
								</Button>
								<ModeToggle dark className="size-12" />
							</div>
						</div>
					)}
				</motion.header>

				{/* Grid content */}
				<motion.div
					className={cn(
						"relative border z-10 shadow-xl border-b-0 grid grid-cols-1 -mt-10 rounded-t-3xl bg-card max-w-4xl mx-auto gap-4 p-4 md:grid-cols-2 md:gap-6 md:p-6 lg:gap-8 lg:p-8",
						isPreview && "md:grid-cols-1",
					)}
					initial={isPreview ? "show" : "hidden"}
					whileInView={isPreview ? undefined : "show"}
					viewport={isPreview ? undefined : { once: true, margin: "-80px" }}
					variants={{
						hidden: { translateY: 200, opacity: 0 },
						show: { translateY: 0, opacity: 1 },
					}}
				>
					{/* Galeria - carousel */}
					<motion.section className="md:col-span-1" variants={cardReveal}>
						<div className="overflow-hidden rounded-2xl border border-border bg-card shadow-xl shadow-border dark:shadow-black/50 md:rounded-3xl">
							{imageUrls.length > 0 ? (
								<Carousel
									autoplay={{ delay: 10000 }}
									className="w-full"
									opts={{ loop: true, duration: 10 }}
								>
									<CarouselContent className="ml-0">
										{imageUrls.map((url) => (
											<CarouselItem key={url} className="pl-0">
												<div className="relative aspect-4/3 w-full md:aspect-4/3">
													<Image
														src={url}
														alt=""
														fill
														className="object-cover"
														sizes="(max-width: 768px) 100vw, 50vw"
														unoptimized={url.startsWith("blob:")}
													/>
												</div>
											</CarouselItem>
										))}
									</CarouselContent>
									<CarouselPrevious className="left-2 top-1/2 -translate-y-1/2 border-border bg-card text-foreground hover:bg-card hover:text-foreground" />
									<CarouselNext className="right-2 top-1/2 -translate-y-1/2 border-border bg-card text-foreground hover:bg-card hover:text-foreground" />
								</Carousel>
							) : (
								<div className="flex aspect-video w-full flex-col items-center justify-center gap-1 rounded-2xl bg-linear-to-br from-rose-50/5 to-rose-200/10 text-sm font-medium md:aspect-4/3">
									<span className={cn(isPreview && "text-sm")}>
										{t("noPhotos")}
									</span>
									{isPreview && (
										<span className="text-xs text-muted-foreground text-center">
											{t("addPhotos")}
										</span>
									)}
								</div>
							)}
						</div>
					</motion.section>

					{/* Mensagem + data */}
					<motion.section
						className="flex flex-col gap-4 md:col-span-1 md:gap-6"
						variants={cardReveal}
					>
						{/* Título com coração */}
						<motion.div
							className="flex items-center gap-2 rounded-2xl border border-border bg-card px-5 py-4 shadow-lg shadow-border dark:shadow-black/50 md:rounded-3xl"
							transition={{ type: "spring", stiffness: 400, damping: 25 }}
						>
							<motion.span
								animate={{ scale: [1, 1.15, 1] }}
								transition={{
									duration: 1.5,
									repeat: Number.POSITIVE_INFINITY,
									repeatDelay: 2,
								}}
							>
								<Heart className="h-5 w-5 text-primary" aria-hidden />
							</motion.span>
							<span
								className={cn(
									"font-semibold text-foreground sm:text-lg",
									isPreview && "text-xs sm:text-sm",
								)}
							>
								{title}
							</span>
						</motion.div>

						{(message || dateLabel) && (
							<div className="flex flex-1 flex-col justify-center rounded-2xl border border-border bg-card p-5 shadow-xl shadow-border dark:shadow-black/50 md:rounded-3xl md:p-6">
								{message && (
									<p
										className={cn(
											"text-foreground sm:text-lg",
											isPreview && "text-xs sm:text-sm",
										)}
									>
										{message}
									</p>
								)}
								{dateLabel && (
									<div
										className={cn(
											"mt-3  flex items-center gap-2 text-sm font-medium text-foreground sm:mt-4 sm:text-base",
											isPreview && "text-xs sm:text-sm",
										)}
									>
										<CalendarDays
											className="h-5 w-5 shrink-0 text-primary"
											aria-hidden
										/>
										<span>
											{t("daysTogether", { days: daysTogether ?? 0 })}
										</span>
									</div>
								)}
							</div>
						)}
					</motion.section>

					{/* Cronômetro do relacionamento */}
					{relationshipDate && (
						<motion.section
							className={isPreview ? "md:col-span-1" : "md:col-span-2"}
							variants={cardReveal}
						>
							<div className="overflow-hidden rounded-2xl md:max-w-lg mx-auto border border-border bg-card shadow-xl shadow-border dark:shadow-black/50 md:rounded-3xl">
								<div className="flex items-center gap-2 border-b border-border bg-card px-4 py-3">
									<Clock className="h-4 w-4 text-primary" aria-hidden />
									<span className="text-sm font-medium text-foreground">
										{t("timeTogether")}
									</span>
								</div>
								<div className="p-4">
									<RelationshipTimer
										relationshipDate={relationshipDate}
										isPreview={isPreview}
									/>
								</div>
							</div>
						</motion.section>
					)}

					{/* Música - full width na grid */}
					{musicUrl && (
						<motion.section
							className={isPreview ? "md:col-span-1" : "md:col-span-2"}
							variants={cardReveal}
						>
							<div className="overflow-hidden rounded-2xl border border-border bg-card shadow-xl shadow-border dark:shadow-black/50 md:rounded-3xl">
								<div className="flex items-center gap-2 border-b border-border bg-card px-4 py-3">
									<Music className="h-4 w-4 text-primary" aria-hidden />
									<span className="text-sm font-medium text-foreground">
										{t("ourMusic")}
									</span>
								</div>
								<div className="p-2 sm:p-4">
									{musicUrl && (
										<iframe
											src={getYoutubeEmbedUrl(musicUrl) ?? ""}
											title="Music"
											className="aspect-video w-full rounded-xl"
											allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
											allowFullScreen
										/>
									)}
								</div>
							</div>
						</motion.section>
					)}
				</motion.div>
			</div>
			{!isPreview && (
				<motion.footer
					className="relative z-40 bg-card"
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true, margin: "-50px" }}
					transition={{ duration: 0.6 }}
				>
					<div className="border-t border-border flex flex-col items-center justify-center gap-4 px-4 py-8 sm:flex-row">
						<div className="flex items-center justify-center gap-1">
							<p className="text-sm text-muted-foreground">{common("madeWith")} </p>
							<Logo />
						</div>
					</div>
				</motion.footer>
			)}
		</>
	);
}
